// Greedy shelf packer for truck loading.
//
// This is a stupid-simple algorithm — NOT an optimal bin-packer. It does
// exactly what a warehouse forklift driver does by eye:
//
//   1. Sort items by footprint length descending (biggest first), so small
//      items can tuck into the last half-row.
//   2. Walk along the truck length. On each "shelf" row, place items
//      across the truck width. When an item doesn't fit in the current row,
//      close the row and start a new one further along the truck.
//   3. If the new row would overflow the truck's length, the item is
//      marked overflow (rendered red in the 3D view, listed in the sidebar).
//
// The algorithm deliberately ignores stacking (height), weight distribution,
// and axle loading. Those are realities a real TMS would handle — this view
// is for "can it physically fit and roughly where does it go".
//
// Coordinate system (to match the three.js scene):
//   - origin is the back-left corner of the truck bed
//   - +X runs along the truck length (toward the cab)
//   - +Z runs across the truck width (toward the right side)
//   - +Y is vertical; items rest on Y=0
//
// Each packed entry returns { item, footprint, x, z, overflow } where
// (x, z) is the CENTER of the item's footprint on the bed.

import { itemFootprint } from './warehouseUtils.js';

// Standard Romanian/EU trailer options. All dimensions interior, in metres.
export const TRUCK_PRESETS = {
  tautliner: {
    id: 'tautliner',
    label: 'Tautliner semi (13.6 × 2.48)',
    lengthM: 13.6,
    widthM: 2.48,
    heightM: 2.7,
  },
  box: {
    id: 'box',
    label: 'Box truck (7.2 × 2.45)',
    lengthM: 7.2,
    widthM: 2.45,
    heightM: 2.4,
  },
  rigid: {
    id: 'rigid',
    label: 'Rigid 18t (9.6 × 2.48)',
    lengthM: 9.6,
    widthM: 2.48,
    heightM: 2.6,
  },
};

export function packIntoTruck(items, truck) {
  const entries = (items || [])
    .map(item => ({
      item,
      orientation: item.orientation,
      footprint: itemFootprint(item, item.orientation),
    }))
    .filter(e => e.footprint)
    // Biggest-length-first so small items fill the tail of each row.
    .sort((a, b) => b.footprint.lengthM - a.footprint.lengthM);

  const placed = [];
  const overflow = [];

  // Row cursor: `rowStartX` is the back edge of the current row;
  // `rowLengthM` is the longest item in the current row (shelf height);
  // `rowFillZ` is how much of the truck width is already used by this row.
  let rowStartX = 0;
  let rowLengthM = 0;
  let rowFillZ = 0;

  for (const entry of entries) {
    const fp = entry.footprint;

    // Two checks: the row still has width for this item, AND growing the
    // row to accommodate its length (if it's longer than what's already
    // placed) wouldn't push past the end of the truck.
    const nextRowLengthM = Math.max(rowLengthM, fp.lengthM);
    const fitsCurrentRow =
      rowFillZ + fp.widthM <= truck.widthM &&
      rowStartX + nextRowLengthM <= truck.lengthM;

    if (fitsCurrentRow) {
      // Place in current row.
      const x = rowStartX + fp.lengthM / 2;
      const z = rowFillZ + fp.widthM / 2;
      placed.push({ ...entry, x, z, overflow: false });
      rowFillZ += fp.widthM;
      rowLengthM = Math.max(rowLengthM, fp.lengthM);
      continue;
    }

    // Close the current row and start a new one.
    rowStartX += rowLengthM;
    rowFillZ = 0;
    rowLengthM = 0;

    if (rowStartX + fp.lengthM > truck.lengthM || fp.widthM > truck.widthM) {
      // Doesn't even fit in a fresh row — overflow.
      overflow.push(entry);
      continue;
    }

    const x = rowStartX + fp.lengthM / 2;
    const z = fp.widthM / 2;
    placed.push({ ...entry, x, z, overflow: false });
    rowFillZ = fp.widthM;
    rowLengthM = fp.lengthM;
  }

  // Floor usage on the same basis the sidebar displays it.
  const totalFootprintM2 = placed.reduce(
    (sum, p) => sum + p.footprint.lengthM * p.footprint.widthM,
    0
  );
  const bedAreaM2 = truck.lengthM * truck.widthM;
  const usedLengthM = placed.reduce(
    (max, p) => Math.max(max, p.x + p.footprint.lengthM / 2),
    0
  );

  return {
    placed,
    overflow,
    stats: {
      placedCount: placed.length,
      overflowCount: overflow.length,
      usedLengthM,
      usedLengthPct: Math.min(100, Math.round((usedLengthM / truck.lengthM) * 100)),
      floorUsagePct: Math.min(100, Math.round((totalFootprintM2 / bedAreaM2) * 100)),
    },
  };
}
